/**
 * UserStatusBadge Component
 * Displays status badges with semantic colors
 */

import { CheckCircle, XCircle } from 'lucide-react';

// ============================================
// Component Props
// ============================================

interface UserStatusBadgeProps {
  isActive: boolean;
}

// ============================================
// UserStatusBadge Component
// ============================================

export const UserStatusBadge: React.FC<UserStatusBadgeProps> = ({ isActive }) => {
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-500">
        <CheckCircle size={14} />
        active
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700 dark:bg-red-500/10 dark:text-red-500">
      <XCircle size={14} />
      suspended
    </span>
  );
};

// ============================================
// Role Badge Component
// ============================================

interface RoleBadgeProps {
  role: 'ADMIN' | 'USER';
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({ role }) => {
  if (role === 'ADMIN') {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700 dark:bg-purple-500/10 dark:text-purple-400">
        admin
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-slate-700 dark:text-slate-300">
      user
    </span>
  );
};

// ============================================
// Plan Badge Component
// ============================================

interface PlanBadgeProps {
  plan?: string;
}

export const PlanBadge: React.FC<PlanBadgeProps> = ({ plan }) => {
  if (!plan) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-slate-700 dark:text-slate-300">
        no plan
      </span>
    );
  }

  if (plan === 'PRO') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
        pro
      </span>
    );
  }

  if (plan === 'ENTERPRISE') {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700 dark:bg-purple-500/10 dark:text-purple-400">
        enterprise
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-slate-700 dark:text-slate-300">
      {plan.toLowerCase()}
    </span>
  );
};
